import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Camera, Upload, Box, Loader2, RotateCcw, AlertTriangle, Ruler, ImagePlus, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { PV_TR } from "@/lib/pipeVizI18n";
import { buildPoints } from "@/lib/pipe3dPoints";
import { ShareModel } from "@/components/ShareModel";
import { MaterialList } from "@/components/MaterialList";
import { SketchCropper } from "@/components/SketchCropper";
import { CutLengths } from "@/components/CutLengths";

const Pipe3D = lazy(() => import("@/components/Pipe3D"));
const MAX_SIDE = 2400;

function compressImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const s = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * s);
      c.height = Math.round(img.height * s);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    img.src = url;
  });
}

function AnalysisCard({ a, setLength, t }) {
  if (!a) return null;
  return (
    <div className="border border-zinc-800 bg-zinc-950 p-4 text-xs text-zinc-300 space-y-2" data-testid="pipeviz-analysis">
      <div className="font-mono uppercase tracking-widest text-[10px] text-amber-400 flex items-center gap-1"><Ruler className="w-3 h-3" /> {t.read}</div>
      <p className="text-zinc-400 leading-relaxed">{a.summary}</p>
      {a.pipe_diameter && <div>{t.diameter}: <b className="text-white">{a.pipe_diameter}</b></div>}
      {!!a.segments?.length && (
        <div className="space-y-1">
          <div>{t.segments}:</div>
          <div className="flex flex-wrap gap-1.5">
            {a.segments.map((s, i) => (
              <input key={i} type="number" inputMode="numeric" value={s.length_mm ?? ""} placeholder="?"
                     onChange={(e) => setLength(i, e.target.value)} data-testid={`pipeviz-length-${i}`}
                     className="w-20 px-1.5 py-1 bg-black border border-zinc-700 focus:border-amber-500 outline-none font-mono text-white" />
            ))}
          </div>
          <div className="text-zinc-500">{t.edit}</div>
        </div>
      )}
      {!!a.bends?.length && <div>{t.bends}: <b className="text-white">{a.bends.length}</b></div>}
    </div>
  );
}

function AiHints({ hints, setHints, t, disabled }) {
  const f = "w-full bg-black border border-zinc-700 focus:border-amber-500 outline-none px-2 py-2 font-mono text-white text-sm placeholder:text-zinc-600";
  const field = (key, label, type, ph) => (
    <label className="flex flex-col gap-1 text-[11px] text-zinc-400">{label}
      <input type={type} inputMode={type === "number" ? "numeric" : "text"} value={hints[key] ?? ""} placeholder={ph} disabled={disabled}
             onChange={(e) => setHints((h) => ({ ...h, [key]: e.target.value }))} data-testid={`pipeviz-hint-${key}`} className={f} />
    </label>
  );
  return (
    <div className="mt-5 border border-amber-500/30 bg-amber-500/5 p-4 max-w-3xl" data-testid="pipeviz-hints">
      <div className="text-xs text-amber-300 mb-3">{t.hintTitle}</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {field("dn", t.hintDn, "text", t.hintDnPh)}
        {field("elbows", t.hintElbows, "number", "?")}
        {field("tees", t.hintTees, "number", "?")}
        {field("a90", t.hintA90, "number", "—")}
      </div>
    </div>
  );
}

function QuotaBar({ quota, freeOut, buyPack, t }) {
  if (!quota || quota.unlimited) return null;
  return (
    <div className="mt-3 text-xs text-zinc-500 flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="pipeviz-quota">
      <span>{quota.remaining > 0 ? t.free2.replace("{n}", quota.remaining).replace("{limit}", quota.limit) : <span className="text-amber-400">{t.out2}</span>}</span>
      {quota.credits > 0 && <span className="text-emerald-400" data-testid="pipeviz-credits">{t.credits.replace("{c}", quota.credits)}</span>}
      {quota.limit < 2 && <Link to="/pricing" className="underline text-amber-300" data-testid="pipeviz-upgrade-link">{t.proHint}</Link>}
      {freeOut && (
        <button onClick={buyPack} data-testid="pipeviz-buy-pack-btn" className="wb-btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1">
          <ShoppingCart className="w-3.5 h-3.5" /> {t.buy}
        </button>
      )}
    </div>
  );
}

export default function PipeVisualizer() {
  const { user, loading } = useAuth();
  const { lang } = useLang();
  const t = PV_TR[lang] || PV_TR.sk;
  const camRef = useRef(null);
  const fileRef = useRef(null);
  const [sketch, setSketch] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [busy, setBusy] = useState(false);
  const [quota, setQuota] = useState(null);
  const [hints, setHints] = useState({});
  const [raw, setRaw] = useState(null);

  useEffect(() => {
    if (!user) return;
    const p = new URLSearchParams(window.location.search);
    const sid = p.get("session_id");
    if (p.get("pack") === "success" && sid) {
      window.history.replaceState({}, "", "/pipe-visualizer");
      let tries = 0;
      const poll = async () => {
        try {
          const r = await api.get(`/pipe-viz/pack-status/${sid}`);
          setQuota(r.data.quota);
          if (r.data.paid) { toast.success(t.packOk); return; }
        } catch { /* retry */ }
        if (++tries < 8) setTimeout(poll, 2500);
      };
      poll();
    } else {
      api.get("/pipe-viz/quota").then((r) => setQuota(r.data)).catch(() => {});
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const has3d = useMemo(() => !!buildPoints(analysis?.segments), [analysis]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  const buyPack = async () => {
    try {
      const r = await api.post("/pipe-viz/buy-pack", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try { setRaw(await compressImage(f)); setSketch(null); setAnalysis(null); } catch { toast.error("Image error"); }
  };

  const start = async () => {
    if (!sketch) return;
    setBusy(true);
    try {
      const int = (v) => (v === undefined || v === "" ? undefined : Math.max(0, Math.min(30, parseInt(v, 10) || 0)));
      const h = { dn: (hints.dn || "").trim().slice(0, 20) || undefined, elbows: int(hints.elbows), tees: int(hints.tees) };
      const r = await api.post("/pipe-viz/analyze", { image_b64: sketch, lang, hints: h }, { timeout: 180000 });
      if (r.data.quota) setQuota(r.data.quota);
      if (r.data.is_pipe_sketch === false) toast.error(t.notSketch);
      else if (!buildPoints(r.data.segments)) toast.error(t.noModel);
      else setAnalysis(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const newSketch = () => {
    setSketch(null);
    setRaw(null);
    setAnalysis(null);
    fileRef.current?.click();
  };

  const setLength = (i, v) => setAnalysis((a) => ({
    ...a, segments: a.segments.map((s, j) => (j === i ? { ...s, length_mm: v === "" ? null : Number(v) } : s)),
  }));

  const freeOut = quota && !quota.unlimited && quota.remaining <= 0 && !(quota.credits > 0);
  const btn = "px-5 py-3 text-sm border border-zinc-700 text-white hover:border-amber-500 transition-colors inline-flex items-center gap-2 disabled:opacity-50";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="pipe-visualizer-page">
      <div className="font-mono text-[10px] tracking-[0.3em] text-amber-400 mb-3">{t.tag}</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white leading-none">{t.title}</h1>
      <p className="text-sm sm:text-base text-zinc-400 mt-4 max-w-2xl">{t.sub}</p>

      <div className="flex flex-wrap gap-3 mt-8">
        <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={onFile} data-testid="pipeviz-camera-input" />
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="pipeviz-file-input" />
        <button onClick={() => camRef.current?.click()} disabled={busy} data-testid="pipeviz-take-photo-btn" className={btn}><Camera className="w-4 h-4" /> {t.take}</button>
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="pipeviz-upload-btn" className={btn}><Upload className="w-4 h-4" /> {t.upload}</button>
        <button onClick={start} disabled={!sketch || busy || !!analysis || freeOut} data-testid="pipeviz-generate-btn"
                className="wb-btn-primary px-5 py-3 text-sm inline-flex items-center gap-2 disabled:opacity-40">
          <Box className="w-4 h-4" /> {t.generate}
        </button>
        {sketch && (
          <button onClick={newSketch} disabled={busy} data-testid="pipeviz-new-sketch-btn" className={btn}>
            <RotateCcw className="w-4 h-4" /> {t.newSketch}
          </button>
        )}
      </div>
      {raw && <SketchCropper src={raw} t={t} onDone={(img) => { setSketch(img); setRaw(null); }} />}
      <AiHints hints={hints} setHints={setHints} t={t} disabled={busy} />
      <QuotaBar quota={quota} freeOut={freeOut} buyPack={buyPack} t={t} />

      <div className="grid lg:grid-cols-[1fr_1.6fr] gap-5 mt-8">
        <div className="space-y-4 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{t.sketch}</div>
          <div className="border border-zinc-800 bg-zinc-950 aspect-[4/3] flex items-center justify-center overflow-hidden" data-testid="pipeviz-sketch-preview">
            {sketch ? <img src={sketch} alt="sketch" className="w-full h-full object-contain" /> : <ImagePlus className="w-10 h-10 text-zinc-700" />}
          </div>
          <AnalysisCard a={analysis} setLength={setLength} t={t} />
        </div>
        <div className="space-y-4 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{t.result}</div>
          <div className="border border-zinc-800 bg-zinc-950 aspect-[4/3] flex items-center justify-center overflow-hidden relative" data-testid="pipeviz-result-preview">
            {busy ? (
              <div className="flex flex-col items-center gap-3 text-zinc-400 text-sm px-6 text-center" data-testid="pipeviz-loading">
                <Loader2 className="w-10 h-10 animate-spin text-amber-400" /> {t.analyzing}
              </div>
            ) : has3d ? (
              <Suspense fallback={<Loader2 className="w-8 h-8 animate-spin text-amber-400" />}>
                <Pipe3D segments={analysis.segments} diameter={analysis.pipe_diameter} fittings={analysis.fittings} />
              </Suspense>
            ) : <Box className="w-10 h-10 text-zinc-700" />}
          </div>
          {has3d && !busy && <div className="text-xs text-zinc-500" data-testid="pipeviz-3d-hint">{t.hint}</div>}
          {has3d && !busy && <ShareModel t={t} segments={analysis.segments} diameter={analysis.pipe_diameter} fittings={analysis.fittings} />}
          {has3d && !busy && <MaterialList segments={analysis.segments} fittings={analysis.fittings} diameter={analysis.pipe_diameter} lang={lang} />}
          {has3d && !busy && <CutLengths segments={analysis.segments} fittings={analysis.fittings} diameter={analysis.pipe_diameter} lang={lang} hintA90={Number(hints.a90) > 0 ? Number(hints.a90) : null} />}
        </div>
      </div>

      <div className="mt-10 flex items-start gap-2 text-xs text-zinc-500 border-t border-zinc-900 pt-5 max-w-3xl" data-testid="pipeviz-disclaimer">
        <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" /> {t.disclaimer}
      </div>
    </div>
  );
}
